/**
 * renderCells —— 单元格控件集合（正常列 + 子项格 + 子列纵向堆叠 + 子表操作列）。
 *
 * 【为何集中在一个文件】对标同仓 `IPCooperationDemand/NegotiationTable/renderCells.tsx`
 * （`ProTablePlusForm/dev.spec.md` 点名的「稳定单元格组件集合」范式）。
 * `CellField` 单独只有 ~30 行，合成一个集合比拆两个文件更省跳转。
 *
 * 【性能铁律】`CellField` / `SubCellContent` 必须是 `React.memo`，且 **props 里的回调必须
 * identity 稳定**（来自 `useControlledMutations` 的 `useMemoizedFn` 返回值）。
 * 在调用点写内联箭头会让 memo 全部失效 → 整行全量重建 → 输入卡顿/失焦。
 */
import React, { useState } from 'react';
import { Button, Checkbox, Select, Tooltip } from '@bedrock/components';
import { AlertCircle } from '@bedrock/icons-react';
import { ComposingSafeInput } from './ComposingSafeInput';
import { CELL_CONTROL_WIDTH_STYLE, CELL_SELECT_DROPDOWN_PROPS } from './cellControlStyle';
import { ADD_ERR_CLASS, ErrorWrap, useDebouncedErrorVisible } from './cellError';
import { subColEditable, subColRules } from './fieldRules';
import type { ChildBase, ChildId, FieldRules, RowId, SubColumnMeta } from './types';

/** 正常列字段控件（input/select，memo）。⚠️ 性能：必须 memo + `onUpdate(rowId, patch)` 稳定回调 */
export const CellField = React.memo(function CellField({ rowId, value, field, type, options, required, rules, onUpdate, forceValidate, form }: {
  rowId: string; value: unknown; field: string; type: 'input' | 'select'; options?: { name: string; value: string }[];
  required?: boolean; rules: FieldRules; onUpdate: (rowId: string, patch: Record<string, unknown>) => void;
  /** 外部全量校验态：true 时全表必填格标红（输入期只看本格是否被编辑过，避免扩散） */
  forceValidate?: boolean;
  /** 表单实例：透传给 ErrorWrap 用于展开 FieldRuleRender（动态规则） */
  form?: unknown;
}) {
  // 输入期校验只对本格生效：避免任一格输入因整表重渲染而让全表必填格一起标红
  const [touched, setTouched] = useState(false);
  // 提示防抖：避免值更新前的过渡帧闪现红框
  const showError = useDebouncedErrorVisible(touched, forceValidate);
  const control = type === 'select' ? (
    <Select size="small" value={(value as string) || undefined} placeholder="请选择" options={options} allowClear onChange={(v) => { setTouched(true); onUpdate(rowId, { [field]: v }); }} style={CELL_CONTROL_WIDTH_STYLE} {...CELL_SELECT_DROPDOWN_PROPS} />
  ) : (
    <ComposingSafeInput size="small" value={(value as string) ?? ''} placeholder="请输入" onChange={(e) => { setTouched(true); onUpdate(rowId, { [field]: e.target.value }); }} style={CELL_CONTROL_WIDTH_STYLE} />
  );
  // 有规则即包 ErrorWrap（与子列 select/input 分支一致）：原实现仅认 `required`，
  // 导致「非必填但配了 enum/min/max/validator 等规则」的 doc 列校验虽会报错、却无任何提示。
  const hasRules = !!(required || rules[field]?.length);
  return hasRules
    ? <ErrorWrap value={value} field={field} rules={rules} showError={showError} form={form}>{control}</ErrorWrap>
    : control;
});

/** 单个子项格内的字段控件（memo，格级：未编辑格的 it/col/onPatch 引用不变即跳过）。
 *  ⚠️ 用 rowId + 格值而非整行 row，被编辑行内未编辑格也能跳过。 */
export const SubCellContent = React.memo(function SubCellContent({ subKey, rowId, it, items, col, onPatch, forceValidate, form }: {
  subKey: string; rowId: string; it: ChildBase; col: SubColumnMeta<ChildBase>;
  /** 该行该子数组的全部子项（含自身）：供需要行内上下文的 custom 控件使用（如 kpi_code 按本行已选禁用） */
  items: ChildBase[];
  onPatch: (rowId: string, subKey: string, childId: ChildId, p: Record<string, unknown>) => void;
  /** 外部全量校验态：true 时全表必填格标红（输入期只看本格是否被编辑过，避免扩散） */
  forceValidate?: boolean;
  /** 表单实例：透传给 ErrorWrap 用于展开 FieldRuleRender（动态规则） */
  form?: unknown;
}): React.ReactElement | null {
  const value = it[col.field];
  // 输入期校验只对本格生效：避免任一格输入因整表重渲染而让全表必填格一起标红
  const [touched, setTouched] = useState(false);
  // 提示防抖：避免值更新前的过渡帧闪现红框
  const showError = useDebouncedErrorVisible(touched, forceValidate);
  const patch = (p: Record<string, unknown>) => {
    setTouched(true);
    onPatch(rowId, subKey, it.id, p);
  };
  switch (col.type) {
    case 'select':
      return <ErrorWrap showError={showError} value={value} field={col.field} rules={subColRules(col)} item={it} form={form}>
        <Select size="small" value={(value as string) || undefined} placeholder="请选择" options={col.options} onChange={(v) => patch({ [col.field]: v })} style={CELL_CONTROL_WIDTH_STYLE} {...CELL_SELECT_DROPDOWN_PROPS} />
      </ErrorWrap>;
    case 'input':
      return <ErrorWrap showError={showError} value={value} field={col.field} rules={subColRules(col)} item={it} form={form}>
        <ComposingSafeInput size="small" value={(value as string) ?? ''} placeholder="请输入" onChange={(e) => patch({ [col.field]: e.target.value })} style={CELL_CONTROL_WIDTH_STYLE} />
      </ErrorWrap>;
    case 'visible':
      return <Button size="small" type="link" className="!p-0" onClick={() => patch({ [col.field]: !(value as boolean) })}>{value ? '可见' : '不可见'}</Button>;
    case 'checkbox':
      return <Checkbox checked={!!(value as boolean)} onChange={(e) => patch({ [col.field]: e.target.checked })}>验收标准</Checkbox>;
    case 'custom': {
      const control = col.render ? col.render(it, patch, items) : null;
      // 必填/带格式校验的自定义列补 ErrorWrap：使 custom 列与内置 select/input 一致地
      // 「实时标红 + hover 提示」。仅在有必要时包裹——custom 列常是属性列这类大批量列，
      // 无条件包裹会平白增加 DOM 层级。
      // 无规则、或该字段当前非受控（只读展示/禁用控件）→ 不标红：
      // 对用户根本改不了的控件报「必填」只会制造无从修复的死锁
      if ((!col.required && !col.validator) || !subColEditable(col, it)) return control ? <>{control}</> : null;
      return (
        <ErrorWrap showError={showError} value={value} field={col.field} rules={subColRules(col)} item={it} form={form}>
          {control}
        </ErrorWrap>
      );
    }
    default:
      return null;
  }
});

/**
 * 子项行：高度由 Tailwind `h-[40px]`(rem) 承担，仅保留水平分隔线。
 *
 * ⚠️ 分隔线必须与**主表自带的分隔线同色**（`--border-color-2`，即主表行分隔/表头分隔用的
 * 那支边框色），不能用 `--border-color-1`：后者是**输入控件边框色**，明显更深，
 * 会让新增的子项分隔线比主表既有线条深一档，看起来像叠了一条。
 *
 * 【左右内边距取 `px-3`(12px)】本仓 Tailwind 间距为 **4px 基准**（`generateSpacing(16,1)`
 * → key N = 4N px），故 12px 记作 `px-3`。子项行与「+ 添加」行必须同值，否则左边缘错位。
 */
export const subItemRowStyle = { borderBottom: '1px solid var(--border-color-2)' };
export const subAddRowStyle: Record<string, unknown> = {};

/** 渲染某子列 cell：纵向排该 subArray 全部子项的该字段，从顶部对齐 */
export function renderSubColCell(
  rowId: RowId,
  subKey: string,
  col: SubColumnMeta<ChildBase>,
  onPatch: (rowId: string, subKey: string, childId: ChildId, p: Record<string, unknown>) => void,
  items: ChildBase[],
  emptyLabel: string,
  showAdd: boolean,
  onAdd: () => void,
  opts?: {
    /** 外部全量校验态：透传给 SubCellContent 用于必填格标红 */
    forceValidate?: boolean;
    /**
     * 「+添加{emptyLabel}」的错误态文案（有值 = 该组整体必填但一条子项都没有）。
     *
     * 子数组为空时格内**没有任何输入控件**——错误无处可标，用户也看不出该做什么。
     * 故把错误态落在唯一可操作的入口上（按钮转红 + Tooltip 引导）。
     * 与 findFirstDoc 的「整体必填且无子项」判定同源，二者同时成立。
     */
    addError?: string;
    /** 表单实例：透传给 SubCellContent 用于展开 FieldRuleRender（动态规则） */
    form?: unknown;
    /**
     * 【子列动作】该子列 cell 底部的「按行取源」动作节点（见 `SubArraySpec.renderRowAction`）。
     *
     * 与添加入口**共用同一个高度槽位**，故哪种落点都不额外占行（`sideHeight` 无需改动）：
     *  - 动作落在**另一子列**（`rowActionColumn: 'second'`，而添加恒在第一子列）→ 两格各占末尾 1 行，视觉并排；
     *  - 动作落在**添加所在子列**（`'first'`，或第二子列不存在时的回退）→ 与「+添加{emptyLabel}」
     *    合并进同一行。
     */
    rowAction?: React.ReactNode;
  },
) {
  const { forceValidate, addError, form, rowAction } = opts || {};
  /**
   * 错误态（容器红框 + 按钮图标）只属于「+添加{emptyLabel}」—— 动作节点不是校验入口。
   * 以 `showAdd` 为前提：动作与添加可能共用同一容器（`rowActionColumn: 'first'`，
   * 或第二子列不存在而回退到第一子列时），若只看 `addError`，
   * 则「整体必填但无子项」会把动作行一并染红。
   */
  const showAddError = showAdd && !!addError;
  // 错误态：按钮转红 + 文字末尾挂 AlertCircle，视觉上比单纯变红更醒目
  //（表格里「红字」被大量列内容稀释，仅靠颜色不足以把视线引到唯一的操作入口上）
  const addButton = (
    <Button size="small" type="text" danger={!!addError} className="!p-0" onClick={onAdd}>
      + 添加{emptyLabel}{addError ? <AlertCircle className="ml-1" /> : null}
    </Button>
  );
  return (
    <div className="flex flex-col w-full">
      {items.length === 0 ? null : items.map((it) => (
        /* [&>*]:w-full —— 子项行是 flex 容器，直接子元素（Form.Item / 裸控件）默认
           flex: 0 1 auto 会被 shrink 到内容宽度，导致窄控件（如 InputNumber 219px）
           铺不满 cell 的 render 区域；显式撑满以对齐主表（其父为 table-cell 不 shrink）。 */
        <div key={it.id} className="flex items-center box-border px-3 h-[40px] [&>*]:w-full" style={subItemRowStyle}>
          <SubCellContent subKey={subKey} rowId={rowId} it={it} items={items} col={col} onPatch={onPatch} forceValidate={forceValidate} form={form} />
        </div>
      ))}
      {(showAdd || rowAction) && (
        /* 添加入口与子列动作共用末尾这一行。
           `gap-2`（8px）**只在两者同时落在此格时可见** —— 即权益两区块（`rowActionColumn: 'first'`），
           故它就是「权益：添加与整单动作之间 8px」的唯一来源。
           ⚠️ 考核指标是**分列**布局（添加在第一子列、动作在第二子列），两格各自只有一个子元素，
           gap 对它天然不生效 ⇒ 无需为此加条件分支，也不要为了「统一」把两者并到同一格。 */
        <div className={`flex items-center gap-2 box-border px-3 h-[40px]${showAddError ? ` ${ADD_ERR_CLASS}` : ''}`} style={subAddRowStyle}>
          {showAdd && (addError ? <Tooltip title={addError}>{addButton}</Tooltip> : addButton)}
          {rowAction}
        </div>
      )}
    </div>
  );
}

/** 渲染操作子列 cell：纵向排该 subArray 全部子项的"删除"按钮 */
export function renderOpColCell(items: ChildBase[], onDelete: (id: ChildId) => void, disabled?: boolean) {
  return (
    <div className="flex flex-col w-full">
      {items.length === 0 ? null : items.map((it) => (
        <div key={it.id} className="flex items-center justify-center box-border h-[40px]" style={subItemRowStyle}>
          <Button size="small" type="text" danger disabled={disabled} onClick={() => onDelete(it.id)}>删除</Button>
        </div>
      ))}
    </div>
  );
}
